import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api, errMsg, fmtCurrency, fmtDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, Search, Download, Trash2, Pencil, Filter, ArrowRightLeft } from "lucide-react";
import { PageHeader, EmptyState } from "@/components/common/Primitives";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function ResourcePage({
  title, subtitle, endpoint, name,
  columns, formFields, defaultValues = {},
  statusOptions = [], emptyMessage, canExport = false,
  relations = [], // ['buyers','products','users']
  renderCell, convert,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const readOnly = user.role === "viewer";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultValues);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [rel, setRel] = useState({ buyers: [], products: [], users: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get(`/${endpoint}`, { params });
      setItems(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [endpoint, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Convert-prefill: when arriving from a convert action, open the new record for editing
  useEffect(() => {
    const editId = location.state?.editId;
    if (editId && items.length) {
      const item = items.find((i) => i.id === editId);
      if (item) {
        setEditing(item);
        setForm({ ...item });
        setDialogOpen(true);
        window.history.replaceState({}, "");
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  useEffect(() => {
    const loaders = {};
    if (relations.includes("buyers")) loaders.buyers = api.get("/buyers");
    if (relations.includes("products")) loaders.products = api.get("/products");
    if (relations.includes("users")) loaders.users = api.get("/users");
    Promise.all(Object.entries(loaders).map(([k, p]) => p.then(({ data }) => [k, data]).catch(() => [k, []])))
      .then((entries) => setRel((prev) => ({ ...prev, ...Object.fromEntries(entries) })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => { setEditing(null); setForm(defaultValues); setDialogOpen(true); };
  const openEdit = (item) => { setEditing(item); setForm({ ...item }); setDialogOpen(true); };

  const save = async () => {
    setSaving(true);
    try {
      if (editing) await api.put(`/${endpoint}/${editing.id}`, form);
      else await api.post(`/${endpoint}`, form);
      toast.success(`${name} ${editing ? "updated" : "created"} successfully`);
      setDialogOpen(false);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/${endpoint}/${deleteId}`);
      toast.success(`${name} deleted`);
      setDeleteId(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doConvert = async (item) => {
    try {
      const { data } = await api.post(`/${convert.endpoint(item.id)}`);
      toast.success(`${convert.toast}: ${data[convert.resultKey] || ""}`);
      navigate(convert.to, { state: { editId: data.id } });
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const doExport = async () => {    try {
      const res = await api.get(`/export/${endpoint}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `${endpoint}.csv`; a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Exported to CSV");
    } catch (e) { toast.error(errMsg(e)); }
  };

  const filtered = items.filter((it) => {
    if (!search) return true;
    return JSON.stringify(it).toLowerCase().includes(search.toLowerCase());
  });

  const relOptions = (src) => {
    if (src === "buyers") return rel.buyers.map((b) => ({ value: b.id, label: b.company_name }));
    if (src === "products") return rel.products.map((p) => ({ value: p.id, label: p.name }));
    if (src === "users") return rel.users.filter((u) => ["salesperson", "sales_manager"].includes(u.role)).map((u) => ({ value: u.id, label: u.name }));
    return [];
  };

  return (
    <div>
      <PageHeader title={title} subtitle={subtitle}>
        {canExport && (
          <Button variant="outline" onClick={doExport} data-testid={`export-${endpoint}-button`} className="gap-2">
            <Download className="h-4 w-4" /> <span className="hidden sm:inline">Export CSV</span>
          </Button>
        )}
        {!readOnly && (
          <Button onClick={openCreate} data-testid={`create-${name.toLowerCase().replace(/\s/g, "-")}-button`} className="gap-2 bg-slate-900 hover:bg-slate-800 text-white">
            <Plus className="h-4 w-4" /> New {name}
          </Button>
        )}
      </PageHeader>

      <Card className="border border-slate-200/80 rounded-xl shadow-sm overflow-hidden">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input data-testid={`${endpoint}-search-input`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}…`} className="pl-9 bg-slate-50 border-slate-200" />
          </div>
          {statusOptions.length > 0 && (
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-48 bg-slate-50 border-slate-200" data-testid={`${endpoint}-status-filter`}>
                <Filter className="h-4 w-4 mr-1 text-slate-400" />
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="all">All statuses</SelectItem>
                {statusOptions.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
          )}
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="py-16 flex justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-200 border-t-sky-500" /></div>
          ) : filtered.length === 0 ? (
            <EmptyState message={emptyMessage} action={!readOnly && <Button onClick={openCreate} className="bg-slate-900 text-white gap-2"><Plus className="h-4 w-4" />New {name}</Button>} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80 hover:bg-slate-50/80">
                  {columns.map((c) => <TableHead key={c.key} className="text-xs uppercase tracking-wider font-semibold text-slate-500 whitespace-nowrap">{c.label}</TableHead>)}
                  <TableHead className="text-right text-xs uppercase tracking-wider font-semibold text-slate-500">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((it) => (
                  <TableRow key={it.id} className="hover:bg-slate-50/60 cursor-pointer" data-testid={`${endpoint}-row-${it.id}`} onClick={() => openEdit(it)}>
                    {columns.map((c) => (
                      <TableCell key={c.key} className="whitespace-nowrap text-sm text-slate-700">
                        {c.type === "status" ? <StatusBadge value={it[c.key]} />
                          : c.type === "currency" ? <span className="font-medium text-slate-900">{fmtCurrency(it[c.key])}</span>
                          : c.type === "mono" ? <span className="font-mono text-xs text-slate-600">{it[c.key]}</span>
                          : c.type === "date" ? fmtDate(it[c.key])
                          : c.type === "percent" ? (it[c.key] != null ? `${it[c.key]}%` : "—")
                          : renderCell ? (renderCell(c, it) ?? (it[c.key] || "—"))
                          : (it[c.key] || "—")}
                      </TableCell>
                    ))}
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center justify-end gap-1">
                        {!readOnly && convert && (
                          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-sky-600 hover:bg-sky-50 hover:text-sky-700" onClick={() => doConvert(it)} data-testid={`convert-${it.id}`}>
                            <ArrowRightLeft className="h-3.5 w-3.5" /> <span className="hidden sm:inline text-xs">{convert.label}</span>
                          </Button>
                        )}
                        {!readOnly && (
                          <>
                            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(it)} data-testid={`edit-${it.id}`}>
                              <Pencil className="h-4 w-4 text-slate-500" />
                            </Button>
                            <Button variant="ghost" size="icon" className="h-8 w-8 hover:bg-rose-50" onClick={() => setDeleteId(it.id)} data-testid={`delete-${it.id}`}>
                              <Trash2 className="h-4 w-4 text-rose-500" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={`${endpoint}-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-display">{editing ? `Edit ${name}` : `New ${name}`}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            {formFields.map((f) => {
              const full = f.full ? "sm:col-span-2" : "";
              const val = form[f.name] ?? "";
              return (
                <div key={f.name} className={`space-y-1.5 ${full}`}>
                  <Label className="text-xs font-medium text-slate-600">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
                  {f.type === "select" || f.type === "relation" ? (
                    <Select value={val ? String(val) : ""} onValueChange={(v) => setForm({ ...form, [f.name]: v })}>
                      <SelectTrigger data-testid={`field-${f.name}`} className="bg-white border-slate-200"><SelectValue placeholder={`Select ${f.label.toLowerCase()}`} /></SelectTrigger>
                      <SelectContent className="bg-white max-h-60">
                        {(f.type === "relation" ? relOptions(f.source) : f.options.map((o) => ({ value: o, label: o }))).map((o) => (
                          <SelectItem key={o.value} value={String(o.value)}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : f.type === "textarea" ? (
                    <Textarea data-testid={`field-${f.name}`} value={val} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })} className="bg-white border-slate-200" rows={3} />
                  ) : (
                    <Input
                      data-testid={`field-${f.name}`}
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      value={f.type === "date" && val ? String(val).slice(0, 10) : val}
                      onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? Number(e.target.value) : e.target.value })}
                      className="bg-white border-slate-200"
                    />
                  )}
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="dialog-cancel-button">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="dialog-save-button">
              {saving ? "Saving…" : editing ? "Save changes" : `Create ${name}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this {name.toLowerCase()}?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-button">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="delete-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
